// routineFormHelpers — shared helpers for the routine form body. Extracted
// to keep the orchestrator under the 200-line CLAUDE.md budget.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type {
  RoutineKindShell,
  RoutineParamDef,
  RoutineParamModeDef,
  RoutineShellDef,
  RoutineSqlAccess,
  RoutineSqlSecurity,
  RoutineVolatility,
} from '../../../../sql-resolver/types/ddlEdit';
import type { ParsedParkedRoutine } from '../../../../sql-resolver/ddl/extract/routineTypes';

export type RoutineEditMode = 'create' | 'alter';

export interface RoutineParamFormValue {
  readonly name: string;
  readonly typeText: string;
  readonly mode: RoutineParamModeDef | '';
  readonly defaultExpr: string;
}

export interface RoutineFormValues {
  readonly kind: RoutineKindShell;
  readonly name: string;
  readonly returnsText: string;
  readonly bodyText: string;
  readonly params: ReadonlyArray<RoutineParamFormValue>;
  readonly mssqlOrAlter: boolean;
  readonly orReplace: boolean;
  readonly pgsqlLanguage: string;
  readonly pgsqlVolatility: RoutineVolatility | '';
  readonly pgsqlStrict: boolean;
  readonly pgsqlSecurity: RoutineSqlSecurity | '';
  readonly mysqlDeterministic: boolean;
  readonly mysqlSqlAccess: RoutineSqlAccess | '';
  readonly mysqlSqlSecurity: RoutineSqlSecurity | '';
  readonly oracleEditionable: boolean;
  readonly oracleNonEditionable: boolean;
  /** Alter-mode action picker. */
  readonly alterAction: 'recompile' | 'rename';
  readonly alterRenameTo: string;
}

export const DEFAULT_ROUTINE_FORM_VALUES: RoutineFormValues = {
  kind: 'procedure',
  name: '',
  returnsText: '',
  bodyText: '',
  params: [],
  mssqlOrAlter: false,
  orReplace: false,
  pgsqlLanguage: 'plpgsql',
  pgsqlVolatility: '',
  pgsqlStrict: false,
  pgsqlSecurity: '',
  mysqlDeterministic: false,
  mysqlSqlAccess: '',
  mysqlSqlSecurity: '',
  oracleEditionable: false,
  oracleNonEditionable: false,
  alterAction: 'recompile',
  alterRenameTo: '',
};

export function valuesFromParsed(parsed: ParsedParkedRoutine): Partial<RoutineFormValues> {
  return {
    kind: parsed.kind,
    name: parsed.name.name,
    ...(parsed.returnsText ? { returnsText: parsed.returnsText } : {}),
    ...(parsed.bodyText ? { bodyText: parsed.bodyText } : {}),
    ...(parsed.params && parsed.params.length > 0
      ? {
          params: parsed.params.map((p) => ({
            name: p.name,
            typeText: p.typeText,
            mode: (p.mode ?? '') as RoutineParamModeDef | '',
            defaultExpr: p.defaultExpr ?? '',
          })),
        }
      : {}),
    ...(parsed.mssqlOrAlter ? { mssqlOrAlter: true } : {}),
    ...(parsed.orReplace ? { orReplace: true } : {}),
    ...(parsed.pgsqlLanguage ? { pgsqlLanguage: parsed.pgsqlLanguage } : {}),
    ...(parsed.oracleEditionable ? { oracleEditionable: true } : {}),
    ...(parsed.oracleNonEditionable ? { oracleNonEditionable: true } : {}),
  };
}

function makeQualifiedName(text: string): QualifiedName {
  const trimmed = text.trim();
  const dotIdx = trimmed.indexOf('.');
  if (dotIdx > 0) {
    const schema = trimmed.slice(0, dotIdx).trim();
    const name = trimmed.slice(dotIdx + 1).trim();
    return {
      schema, name, raw: trimmed, quoted: false,
      foldedName: name.toLowerCase(),
      foldedSchema: schema.toLowerCase(),
    };
  }
  return {
    name: trimmed, raw: trimmed, quoted: false,
    foldedName: trimmed.toLowerCase(),
  };
}

export function makeRoutineQualifiedName(text: string): QualifiedName {
  return makeQualifiedName(text);
}

function paramFromForm(p: RoutineParamFormValue): RoutineParamDef {
  return {
    name: p.name.trim(),
    typeText: p.typeText.trim(),
    ...(p.mode ? { mode: p.mode as RoutineParamModeDef } : {}),
    ...(p.defaultExpr.trim().length > 0 ? { defaultExpr: p.defaultExpr.trim() } : {}),
  };
}

export function buildRoutineShellDef(
  engine: SqlEngine, values: RoutineFormValues,
): RoutineShellDef {
  const params = values.params.map(paramFromForm).filter((p) => p.name.length > 0);
  return {
    kind: values.kind,
    name: makeQualifiedName(values.name),
    params,
    ...(values.kind === 'function' && values.returnsText.trim().length > 0
      ? { returnsText: values.returnsText.trim() } : {}),
    bodyText: values.bodyText,
    ...(values.mssqlOrAlter && engine === 'mssql' ? { mssqlOrAlter: true } : {}),
    ...(values.orReplace && (engine === 'postgres' || engine === 'oracle')
      ? { orReplace: true } : {}),
    ...(engine === 'postgres' && values.pgsqlLanguage.trim().length > 0
      ? { pgsqlLanguage: values.pgsqlLanguage.trim() } : {}),
    ...(engine === 'postgres' && values.kind === 'function' && values.pgsqlVolatility
      ? { pgsqlVolatility: values.pgsqlVolatility as RoutineVolatility } : {}),
    ...(engine === 'postgres' && values.kind === 'function' && values.pgsqlStrict
      ? { pgsqlStrict: true } : {}),
    ...(engine === 'postgres' && values.pgsqlSecurity
      ? { pgsqlSecurity: values.pgsqlSecurity as RoutineSqlSecurity } : {}),
    ...(engine === 'mysql' && values.mysqlDeterministic ? { mysqlDeterministic: true } : {}),
    ...(engine === 'mysql' && values.mysqlSqlAccess
      ? { mysqlSqlAccess: values.mysqlSqlAccess as RoutineSqlAccess } : {}),
    ...(engine === 'mysql' && values.mysqlSqlSecurity
      ? { mysqlSqlSecurity: values.mysqlSqlSecurity as RoutineSqlSecurity } : {}),
    ...(engine === 'oracle' && values.oracleEditionable ? { oracleEditionable: true } : {}),
    ...(engine === 'oracle' && values.oracleNonEditionable
      ? { oracleNonEditionable: true } : {}),
  };
}

export const KIND_OPTIONS: ReadonlyArray<{ value: RoutineKindShell; label: string }> = [
  { value: 'procedure', label: 'PROCEDURE' },
  { value: 'function', label: 'FUNCTION' },
];

export const PARAM_MODE_OPTIONS: ReadonlyArray<{
  value: '' | RoutineParamModeDef; label: string;
}> = [
  { value: '', label: 'IN (default)' },
  { value: 'in', label: 'IN' },
  { value: 'out', label: 'OUT' },
  { value: 'inout', label: 'INOUT' },
];

export const VOLATILITY_OPTIONS: ReadonlyArray<{
  value: '' | RoutineVolatility; label: string;
}> = [
  { value: '', label: '— default —' },
  { value: 'volatile', label: 'VOLATILE' },
  { value: 'stable', label: 'STABLE' },
  { value: 'immutable', label: 'IMMUTABLE' },
];

export const SECURITY_OPTIONS: ReadonlyArray<{
  value: '' | RoutineSqlSecurity; label: string;
}> = [
  { value: '', label: '— default —' },
  { value: 'definer', label: 'DEFINER' },
  { value: 'invoker', label: 'INVOKER' },
];

export const SQL_ACCESS_OPTIONS: ReadonlyArray<{
  value: '' | RoutineSqlAccess; label: string;
}> = [
  { value: '', label: '— default —' },
  { value: 'no-sql', label: 'NO SQL' },
  { value: 'reads-sql-data', label: 'READS SQL DATA' },
  { value: 'modifies-sql-data', label: 'MODIFIES SQL DATA' },
  { value: 'contains-sql', label: 'CONTAINS SQL' },
];
